"use client"

import { useState } from "react"
import { Card, Button, Form, Spinner, Row, Col } from "react-bootstrap"
import DashboardLayout from "../../components/Layout/DashboardLayout"
import api from "../../services/api"
import Breadcrumbs from "../../components/Layout/Breadcrumbs"

export default function CreateQrCodePage() {
  const [originalUrl, setOriginalUrl] = useState("")
  const [color, setColor] = useState("#3b82f6")
  const [logo, setLogo] = useState("")
  const [template, setTemplate] = useState("")
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    setSuccess(false)

    try {
      const payload = {
        originalUrl,
        qrStyle: {
          color,
          logo,
          template,
        },
      }

      const res = await api.post("/links", payload)

      if (res.data.success) {
        setSuccess(true)
        setTimeout(() => setSuccess(false), 3000)
      } else {
        setError(res.data.message)
      }
    } catch (err) {
      setError("Failed to create QR Code")
    } finally {
      setLoading(false)
    }
  }

  const breadcrumbItems = [
    { name: "Dashboard", path: "/user-dashboard" },
    { name: "Dynamic QR Codes", path: "/qrcodes/dynamic" },
    { name: "Create QR Code" },
  ]

  const templates = [
    {
      value: "",
      label: "Default",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
        </svg>
      ),
    },
    {
      value: "modern",
      label: "Modern",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        </svg>
      ),
    },
    {
      value: "rounded",
      label: "Rounded",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="4" />
        </svg>
      ),
    },
  ]

  return (
    <DashboardLayout>
      <div className="container mt-4" style={{ maxWidth: "1000px" }}>
        <Breadcrumbs items={breadcrumbItems} />

        {/* Header */}
        <div className="mb-4">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                backgroundColor: "#eff6ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#3b82f6",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            </div>
            <div>
              <h3 className="mb-1" style={{ fontWeight: "600", color: "#1e293b" }}>
                Create QR Code
              </h3>
              <p className="mb-0" style={{ color: "#64748b", fontSize: "14px" }}>
                Generate a branded QR code with custom styling
              </p>
            </div>
          </div>
        </div>

        <Row>
          <Col lg={7}>
            <Card
              className="border-0 mb-4"
              style={{
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}
            >
              {/* Card Header */}
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #e2e8f0",
                  backgroundColor: "#f8fafc",
                }}
              >
                <h5 className="mb-0" style={{ fontWeight: "600", color: "#1e293b", fontSize: "15px" }}>
                  QR Code Details
                </h5>
              </div>

              <Card.Body className="p-4">
                {/* Success Alert */}
                {success && (
                  <div
                    className="mb-4 d-flex align-items-center gap-2"
                    style={{
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      borderRadius: "8px",
                      color: "#166534",
                      padding: "12px 16px",
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span style={{ fontWeight: "500", fontSize: "14px" }}>QR Code created successfully!</span>
                  </div>
                )}

                {/* Error Alert */}
                {error && (
                  <div
                    className="mb-4 d-flex align-items-center gap-2"
                    style={{
                      backgroundColor: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: "8px",
                      color: "#dc2626",
                      padding: "12px 16px",
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span style={{ fontWeight: "500", fontSize: "14px" }}>{error}</span>
                  </div>
                )}

                <Form onSubmit={handleSubmit}>
                  {/* Destination URL */}
                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: "500", color: "#374151", fontSize: "14px", marginBottom: "8px" }}>
                      Destination URL
                    </Form.Label>
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          left: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#9ca3af",
                          zIndex: 1,
                        }}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                        </svg>
                      </span>
                      <Form.Control
                        type="url"
                        required
                        value={originalUrl}
                        onChange={(e) => setOriginalUrl(e.target.value)}
                        placeholder="https://example.com"
                        style={{
                          paddingLeft: "42px",
                          height: "44px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          backgroundColor: "#fff",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                  </Form.Group>

                  {/* QR Code Color */}
                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: "500", color: "#374151", fontSize: "14px", marginBottom: "8px" }}>
                      QR Code Color
                    </Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      <Form.Control
                        type="color"
                        value={color}
                        onChange={(e) => setColor(e.target.value)}
                        style={{
                          width: "60px",
                          height: "44px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          cursor: "pointer",
                          padding: "4px",
                        }}
                      />
                      <div
                        style={{
                          flex: 1,
                          padding: "10px 14px",
                          backgroundColor: "#f8fafc",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          fontFamily: "monospace",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#64748b",
                        }}
                      >
                        {color.toUpperCase()}
                      </div>
                    </div>
                  </Form.Group>

                  {/* Logo URL */}
                  <Form.Group className="mb-4">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Form.Label className="mb-0" style={{ fontWeight: "500", color: "#374151", fontSize: "14px" }}>
                        Logo URL
                      </Form.Label>
                      <span
                        style={{
                          fontSize: "11px",
                          padding: "2px 8px",
                          backgroundColor: "#f1f5f9",
                          color: "#64748b",
                          borderRadius: "4px",
                          fontWeight: "500",
                        }}
                      >
                        Optional
                      </span>
                    </div>
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          position: "absolute",
                          left: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#9ca3af",
                          zIndex: 1,
                        }}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      </span>
                      <Form.Control
                        type="url"
                        value={logo}
                        onChange={(e) => setLogo(e.target.value)}
                        placeholder="https://cdn.site/logo.png"
                        style={{
                          paddingLeft: "42px",
                          height: "44px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                          backgroundColor: "#fff",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                  </Form.Group>

                  {/* Template Style */}
                  <Form.Group className="mb-4">
                    <Form.Label style={{ fontWeight: "500", color: "#374151", fontSize: "14px", marginBottom: "12px" }}>
                      Template Style
                    </Form.Label>
                    <Row className="g-3">
                      {templates.map((temp) => (
                        <Col xs={4} key={temp.value}>
                          <div
                            onClick={() => setTemplate(temp.value)}
                            style={{
                              padding: "16px 12px",
                              borderRadius: "8px",
                              border: template === temp.value ? "2px solid #3b82f6" : "1px solid #e2e8f0",
                              backgroundColor: template === temp.value ? "#eff6ff" : "#fff",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                marginBottom: "8px",
                                color: template === temp.value ? "#3b82f6" : "#64748b",
                              }}
                            >
                              {temp.icon}
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "500",
                                color: template === temp.value ? "#3b82f6" : "#374151",
                              }}
                            >
                              {temp.label}
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Form.Group>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: "100%",
                      height: "44px",
                      borderRadius: "8px",
                      border: "none",
                      backgroundColor: loading ? "#94a3b8" : "#3b82f6",
                      color: "white",
                      fontWeight: "500",
                      fontSize: "14px",
                      transition: "background-color 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) e.currentTarget.style.backgroundColor = "#2563eb"
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) e.currentTarget.style.backgroundColor = "#3b82f6"
                    }}
                  >
                    {loading ? (
                      <span className="d-flex align-items-center justify-content-center gap-2">
                        <Spinner size="sm" animation="border" />
                        <span>Creating...</span>
                      </span>
                    ) : (
                      <span className="d-flex align-items-center justify-content-center gap-2">
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        <span>Create QR Code</span>
                      </span>
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* Preview Column */}
          <Col lg={5}>
            <Card
              className="border-0 sticky-top"
              style={{
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                top: "20px",
              }}
            >
              <Card.Body className="p-4">
                <div className="mb-3">
                  <h6 style={{ fontWeight: "600", color: "#1e293b", marginBottom: "4px" }}>QR Code Preview</h6>
                  <p className="mb-0" style={{ color: "#64748b", fontSize: "13px" }}>
                    Preview how your QR code will look
                  </p>
                </div>

                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    padding: "32px",
                    textAlign: "center",
                    border: "1px dashed #cbd5e1",
                  }}
                >
                  <div
                    style={{
                      width: "180px",
                      height: "180px",
                      margin: "0 auto",
                      backgroundColor: "white",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #e2e8f0",
                      color: "#cbd5e1",
                    }}
                  >
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="7" height="7" rx="1" />
                      <rect x="14" y="3" width="7" height="7" rx="1" />
                      <rect x="3" y="14" width="7" height="7" rx="1" />
                      <rect x="14" y="14" width="7" height="7" rx="1" />
                    </svg>
                  </div>
                  <p className="mt-3 mb-0" style={{ color: "#64748b", fontSize: "13px" }}>
                    QR code will appear here after creation
                  </p>
                </div>

                {/* Pro Tip */}
                <div
                  className="mt-4 p-3"
                  style={{
                    backgroundColor: "#eff6ff",
                    borderRadius: "8px",
                    border: "1px solid #bfdbfe",
                  }}
                >
                  <div className="d-flex align-items-start gap-2">
                    <span style={{ color: "#3b82f6", marginTop: "2px" }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                      </svg>
                    </span>
                    <div>
                      <h6 style={{ fontSize: "13px", fontWeight: "600", color: "#1e40af", marginBottom: "4px" }}>
                        Pro Tip
                      </h6>
                      <p className="mb-0" style={{ fontSize: "12px", color: "#64748b" }}>
                        Add your logo to make your QR code more recognizable and branded
                      </p>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </DashboardLayout>
  )
}